import { GetState, SetState } from 'zustand';

import {
  CallRecordingSetting,
  OnePartyConsentRecording,
  TwoPartyConsentRecording,
} from '../../api/graphql/types';
import { Store } from '../useStore';
import {
  callRecordingToggleSettings,
  callRecordingRequired,
  shouldDisplayCallRecordingNotSetupTooltip,
  getStoredCallRecordedPreference,
  saveCallRecordedPreference,
  clearCallRecordedPreference,
} from '../../utils/callRecordingUtils';

export type CallRecordingStore = {
  oneSidedCallRecordingFeatureFlag: boolean;

  callRecorded: boolean;
  setCallRecorded: (callRecorded: boolean) => void;

  numberRequiresConsent: boolean;
  setNumberRequiresConsent: (numberRequiresConsent: boolean) => void;

  isNumberValidated: boolean;
  setIsNumberValidated: (isNumberValidated: boolean) => void;

  callRecordingSetting: CallRecordingSetting | null | undefined;
  onePartyConsentRecordingSetting: OnePartyConsentRecording | null | undefined;
  twoPartyConsentRecordingSetting: TwoPartyConsentRecording | null | undefined;

  callRecordingToggleVisible: boolean;
  callRecordingToggleDisabled: boolean;
  callRecordingRequired: boolean;
  displayCallRecordingNotSetupTooltip: boolean;

  initializeCallRecordingSettings: (
    callRecordingSetting: CallRecordingSetting | null | undefined,
    onePartyConsentRecordingSetting:
      | OnePartyConsentRecording
      | null
      | undefined,
    twoPartyConsentRecordingSetting:
      | TwoPartyConsentRecording
      | null
      | undefined,
    oneSidedCallRecordingFeatureFlag: boolean,
  ) => void;
};

export const callRecordingStore = (
  set: SetState<Store>,
  get: GetState<Store>,
): CallRecordingStore => ({
  callRecorded: false,
  numberRequiresConsent: false,
  isNumberValidated: false,
  callRecordingSetting: null,
  onePartyConsentRecordingSetting: null,
  twoPartyConsentRecordingSetting: null,
  oneSidedCallRecordingFeatureFlag: false,

  callRecordingToggleVisible: true,
  callRecordingToggleDisabled: true,
  callRecordingRequired: false,
  displayCallRecordingNotSetupTooltip: true,

  setCallRecorded: callRecorded => {
    // Only save the updated value if the toggle is not disabled by settings
    const { callRecordingToggleDisabled } = get();
    if (!callRecordingToggleDisabled) {
      set({ callRecorded });
      saveCallRecordedPreference(callRecorded);
    }
  },

  setNumberRequiresConsent: numberRequiresConsent => {
    set({ numberRequiresConsent });

    const {
      callRecordingSetting,
      onePartyConsentRecordingSetting,
      twoPartyConsentRecordingSetting,
      oneSidedCallRecordingFeatureFlag,
      callRecorded,
    } = get();

    const settings = callRecordingToggleSettings(
      oneSidedCallRecordingFeatureFlag,
      callRecordingSetting,
      onePartyConsentRecordingSetting,
      twoPartyConsentRecordingSetting,
      numberRequiresConsent,
    );

    const isCallRecordingRequired = callRecordingRequired(
      oneSidedCallRecordingFeatureFlag,
      settings.callRecordingToggleDisabled,
      settings.initialCallRecordingToggleValue,
      callRecordingSetting,
    );

    set({
      callRecordingToggleVisible: settings.callRecordingToggleVisible,
      callRecordingToggleDisabled: settings.callRecordingToggleDisabled,
      callRecordingRequired: isCallRecordingRequired,
      // Persist the saved user preference unless the settings dictate otherwise
      callRecorded: settings.callRecordingToggleDisabled
        ? settings.initialCallRecordingToggleValue
        : callRecorded,
    });
  },

  setIsNumberValidated: isNumberValidated => {
    set({ isNumberValidated });
  },

  initializeCallRecordingSettings: (
    callRecordingSetting,
    onePartyConsentRecordingSetting,
    twoPartyConsentRecordingSetting,
    oneSidedCallRecordingFeatureFlag,
  ) => {
    const displayCallRecordingNotSetupTooltip =
      shouldDisplayCallRecordingNotSetupTooltip(
        oneSidedCallRecordingFeatureFlag,
        onePartyConsentRecordingSetting,
        twoPartyConsentRecordingSetting,
        callRecordingSetting,
      );

    set({
      oneSidedCallRecordingFeatureFlag,
      callRecordingSetting,
      onePartyConsentRecordingSetting,
      twoPartyConsentRecordingSetting,
      displayCallRecordingNotSetupTooltip,
    });

    const { numberRequiresConsent } = get();

    const settings = callRecordingToggleSettings(
      oneSidedCallRecordingFeatureFlag,
      callRecordingSetting,
      onePartyConsentRecordingSetting,
      twoPartyConsentRecordingSetting,
      numberRequiresConsent,
    );

    const isCallRecordingRequired = callRecordingRequired(
      oneSidedCallRecordingFeatureFlag,
      settings.callRecordingToggleDisabled,
      settings.initialCallRecordingToggleValue,
      callRecordingSetting,
    );

    // Determine if we should respect the local storage preference or use API settings
    // We only respect local storage preferences when MMO settings allow user to control it
    const shouldRespectLocalStorage =
      !settings.callRecordingToggleDisabled && !isCallRecordingRequired;

    if (
      !shouldRespectLocalStorage &&
      getStoredCallRecordedPreference() !== null
    ) {
      clearCallRecordedPreference();
    }

    const savedPreference = shouldRespectLocalStorage
      ? getStoredCallRecordedPreference()
      : null;

    const initialCallRecordedValue =
      savedPreference !== null && shouldRespectLocalStorage
        ? savedPreference
        : settings.initialCallRecordingToggleValue;

    set({
      callRecordingToggleVisible: settings.callRecordingToggleVisible,
      callRecordingToggleDisabled: settings.callRecordingToggleDisabled,
      callRecordingRequired: isCallRecordingRequired,
      callRecorded: initialCallRecordedValue,
    });
  },
});
